import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const ORANGE = '#f57c00';
const LIGHT_BG = '#fff8f2';

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const date = new Date();
  date.setHours(h, m);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const getShiftCategory = (shiftDateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const shiftDate = new Date(shiftDateStr);
  shiftDate.setHours(0, 0, 0, 0);
  if (shiftDate.getTime() === today.getTime()) return 'today';
  if (shiftDate > today) return 'upcoming';
  return 'completed';
};

const Badge = ({ label, color, bg }) => (
  <span style={{
    background: bg, color, padding: '3px 12px',
    borderRadius: 20, fontSize: 12, fontWeight: 600, letterSpacing: 0.3
  }}>
    {label}
  </span>
);

const SectionHeader = ({ title, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '28px 0 12px' }}>
    <h3 style={{ color: ORANGE, margin: 0 }}>{title}</h3>
    <span style={{
      background: ORANGE, color: 'white', borderRadius: 20,
      padding: '1px 10px', fontSize: 12, fontWeight: 700
    }}>{count}</span>
  </div>
);

const ShiftCard = ({ s }) => {
  const category = getShiftCategory(s.shift_date);
  const badgeMap = {
    today:     { label: '📍 Today',     color: '#1565c0', bg: '#e3f2fd' },
    upcoming:  { label: '🕐 Upcoming',  color: '#6a1b9a', bg: '#f3e5f5' },
    completed: { label: '✅ Completed', color: '#2e7d32', bg: '#e8f5e9' },
  };

  return (
    <div style={{
      background: 'white', padding: '16px 20px', borderRadius: 12,
      borderLeft: `4px solid ${ORANGE}`, boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>{s.shift_name}</span>
          <Badge {...badgeMap[category]} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', fontSize: 13, color: '#555' }}>
          <span>📋 <b>{s.exam_name}</b></span>
          <span>🏷 {s.exam_type}</span>
          <span>📅 {formatDate(s.shift_date)}</span>
          <span>⏰ {formatTime(s.start_time)} – {formatTime(s.end_time)}</span>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, color, flex2, prefix }) => (
  <div style={{
    background: 'white', padding: '16px 20px', borderRadius: 12,
    flex: flex2 ? 2 : 1, minWidth: flex2 ? 180 : 120,
    borderLeft: `5px solid ${color || ORANGE}`,
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)'
  }}>
    <div style={{ fontSize: 13, color: '#888', marginBottom: 6, fontWeight: 500 }}>{title}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: color || ORANGE }}>
      {prefix}{value}
    </div>
  </div>
);

const TestCenterDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading]           = useState(true);
  const [schedules, setSchedules]       = useState([]);
  const [shifts, setShifts]             = useState([]);
  const [remuneration, setRemuneration] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sRes, shRes, remRes] = await Promise.all([
        axios.get('/schedules'),
        axios.get('/shifts'),
        axios.get('/remuneration/my')   // ← only this TCA's remuneration
      ]);
      setSchedules(sRes.data || []);
      setShifts(shRes.data || []);
      setRemuneration(remRes.data?.total ?? 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const todayShifts     = shifts.filter(s => getShiftCategory(s.shift_date) === 'today');
  const upcomingShifts  = shifts.filter(s => getShiftCategory(s.shift_date) === 'upcoming');
  const completedShifts = shifts.filter(s => getShiftCategory(s.shift_date) === 'completed');

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading...</div>
  );

  return (
    <div style={{ padding: 30, background: LIGHT_BG, minHeight: '100vh' }}>

      {/* HEADER */}
      <h2 style={{ color: ORANGE, marginBottom: 4 }}>Welcome, {user?.name}</h2>
      <p style={{ color: '#666', marginBottom: 20 }}>Manage schedules, shifts & invigilators</p>

      {/* QUICK ACTIONS */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <button onClick={() => navigate('/schedules')} style={btn(ORANGE)}>📅 View Schedules</button>
        <button onClick={() => navigate('/shifts')}    style={btn('#1565c0')}>🕐 View Shifts</button>
      </div>

      {/* SUMMARY CARDS */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard title="Total Schedules"  value={schedules.length} />
        <StatCard title="Total Shifts"     value={shifts.length} />
        <StatCard title="Today"            value={todayShifts.length} />
        <StatCard title="Upcoming Shifts"  value={upcomingShifts.length} />
        <StatCard title="Completed Shifts" value={completedShifts.length} />
        <StatCard
          title="My Remuneration"
          value={Number(remuneration).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          color="#2e7d32"
          flex2
          prefix="₹"
        />
      </div>

      {/* TODAY'S SHIFTS */}
      {todayShifts.length > 0 && (
        <>
          <SectionHeader title="📍 Today's Shifts" count={todayShifts.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {todayShifts.map(s => <ShiftCard key={s.id} s={s} />)}
          </div>
        </>
      )}

      {/* UPCOMING SHIFTS */}
      {upcomingShifts.length > 0 && (
        <>
          <SectionHeader title="🕐 Upcoming Shifts" count={upcomingShifts.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {upcomingShifts.map(s => <ShiftCard key={s.id} s={s} />)}
          </div>
        </>
      )}

      {/* COMPLETED SHIFTS */}
      {completedShifts.length > 0 && (
        <>
          <SectionHeader title="✅ Completed Shifts" count={completedShifts.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {completedShifts.map(s => <ShiftCard key={s.id} s={s} />)}
          </div>
        </>
      )}

      {shifts.length === 0 && (
        <p style={{ color: '#999', marginTop: 30 }}>No shifts found.</p>
      )}

      {/* OPERATIONAL ACTIONS */}
      <div style={{ marginTop: 30, background: 'white', padding: 20, borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
        <h3 style={{ color: ORANGE, marginTop: 0 }}>Operational Actions</h3>
        <ul style={{ color: '#555', lineHeight: 2 }}>
          <li>Assign invigilators to shifts</li>
          <li>Mark attendance</li>
          <li>Replace absent invigilator</li>
        </ul>
      </div>

    </div>
  );
};

const btn = (color) => ({
  background: color, color: 'white', border: 'none',
  padding: '10px 18px', borderRadius: 8, cursor: 'pointer',
  fontWeight: 600, fontSize: 14
});

export default TestCenterDashboard;